import {
  Button,
  Callout,
  FormField,
  Modal,
  TextInput,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { AnalyticalFieldValueMappingSelector } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldValueMappingSelector';
import {
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldValueMappingV2,
} from 'src/core/modules/bookkeep';

import {
  type FormValues,
  validateCustomFieldsModalForm,
} from '../../validators';

type Props = {
  isOpen: boolean;
  value: string;
  customFieldValues: string[];
  onClose: () => void;
  onSubmit: (value: string) => void;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  valueMapping?: AnalyticalFieldValueMappingV2;
  setValueMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2 | undefined>
  >;
  isMapSyncEnabled: boolean;
  syncMode?: SyncMode;
  valueId: string;
  valueInDatabase?: AnalyticalFieldValueMappingV2;
};

export const UpdateCustomFieldValueModal = ({
  isOpen,
  onClose,
  onSubmit,
  value,
  customFieldValues,
  fieldMapping,
  valueMapping,
  setValueMapping,
  isMapSyncEnabled,
  syncMode,
  valueId,
  valueInDatabase,
}: Props) => {
  const { t } = useTranslation('global');
  const formikProps = useFormik<FormValues>({
    initialValues: {
      value,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validate: (values) => {
      return validateCustomFieldsModalForm(
        values,
        customFieldValues,
        t,
        isMapSyncEnabled,
      );
    },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      await onSubmit(values.value);
      setSubmitting(false);
      onClose();
    },
  });

  useEffect(() => {
    const valueToSelect = fieldMapping?.values.find(
      (fieldValue) => fieldValue.internalId === valueId,
    );
    setValueMapping(valueToSelect);
  }, [isOpen, valueId]);

  return (
    <Modal
      iconName="circle-information"
      iconVariant="info"
      isOpen={isOpen}
      actions={[
        <Button
          variant="secondaryNeutral"
          text={t('customFields.settings.modals.edition.cancel')}
          onClick={() => onClose()}
          key="cancel"
        />,
        <Button
          variant="primaryBrand"
          text={t('customFields.settings.modals.edition.submit')}
          onClick={async () => {
            await formikProps.handleSubmit();
          }}
          isLoading={formikProps.isSubmitting}
          key="update"
        />,
      ]}
      title={t('customFields.settings.modals.edition.title')}
    >
      <FormField
        label={t('customFields.settings.modals.edition.label')}
        alertMessage={formikProps.errors.value}
      >
        <TextInput
          value={formikProps.values.value}
          placeholder={t('customFields.settings.modals.edition.placeholder')}
          onChange={(event) => {
            formikProps.setFieldValue('value', event.target.value);
          }}
        />
      </FormField>
      <Callout
        className="mt-24 text-left"
        variant="info"
        title={t('customFields.settings.modals.edition.callout')}
      />

      {isMapSyncEnabled && fieldMapping && fieldMapping.internalId && (
        <AnalyticalFieldValueMappingSelector
          selectedValue={
            valueMapping && {
              ...valueMapping,
              label: valueMapping.externalName,
              key: valueMapping.id,
            }
          }
          values={fieldMapping.values}
          onValueSelected={(selectedValue) => {
            setValueMapping(selectedValue);
          }}
          syncMode={syncMode}
          valueInDatabase={
            valueInDatabase && {
              ...valueInDatabase,
              label: valueInDatabase.externalName,
              key: valueInDatabase.id,
            }
          }
        />
      )}
    </Modal>
  );
};
